import type { ApiEstimateLineItem, VehicleSection, VehicleSide } from "@/lib/api/types"
import type { DamageZone } from "./types"
import { DAMAGE_ZONE_COPY } from "./copy"

// Turns raw estimate line items into the consumer AHA view-model. CRITICAL:
// this projection STRUCTURALLY omits every cost field (min/max/avg/totalCost/
// manHours). The consumer never sees a price at intake — that's the shop's job,
// later, on the tracking page. Keeping cost out of the type makes it impossible
// to accidentally render a dollar figure in the reveal.

export type Severity = "minor" | "moderate" | "severe"
export type RepairAction = "repair" | "replace"

export interface DetectedArea {
  zone: DamageZone | null // null = couldn't map to the silhouette, still listed
  label: string
  severity: Severity
  action: RepairAction | null
  highConfidence: boolean
}

export interface DamageReadout {
  areas: DetectedArea[]
  zones: DamageZone[] // unique mappable zones, for the annotated silhouette
  headline: string // plain-language summary, e.g. "Front-end impact"
  worst: Severity
}

function sideHasLeft(side: VehicleSide | null): boolean {
  return !!side && side.includes("LEFT")
}
function sideHasRight(side: VehicleSide | null): boolean {
  return !!side && side.includes("RIGHT")
}

// Map a line item's section (+ side for L/R panels) to a silhouette zone.
export function mapToZone(li: {
  section: VehicleSection
  side: VehicleSide | null
  component?: string | null
}): DamageZone | null {
  const { section, side } = li
  switch (section) {
    case "FRONT_BUMPER_GRILLE":
      return "front-bumper"
    case "REAR_BUMPER":
      return "rear-bumper"
    case "HOOD":
      return "hood"
    case "ROOF":
      return "roof"
    case "WINDSHIELD":
      return "windshield"
    case "TRUNK":
    case "TRUNK_LID":
    case "LIFT_GATE":
    case "TAIL_GATE":
      return "trunk"
    case "FENDER":
      return sideHasRight(side) ? "front-fender-right" : "front-fender-left"
    case "FRONT_DOOR":
      return sideHasRight(side) ? "front-door-right" : "front-door-left"
    case "REAR_DOOR":
      return sideHasRight(side) ? "rear-door-right" : "rear-door-left"
    case "QUARTER_PANEL":
      return sideHasRight(side) ? "quarter-panel-right" : "quarter-panel-left"
    default:
      // FRONT_LAMPS, REAR_LAMPS, ROCKER_PANEL, WHEELS, FRAME, PICKUP_BOX —
      // no dedicated silhouette zone; still surfaced in the area list.
      return null
  }
}

function severityFrom(li: {
  severity: number | null
  damageExtent: string | null
}): Severity {
  const ext = (li.damageExtent ?? "").toLowerCase()
  if (ext.includes("severe") || ext.includes("major")) return "severe"
  if (ext.includes("moderate")) return "moderate"
  if (ext.includes("minor") || ext.includes("light")) return "minor"
  // Fall back to the numeric 1-10 severity.
  const n = li.severity ?? 0
  if (n >= 7) return "severe"
  if (n >= 4) return "moderate"
  return "minor"
}

function actionFrom(action: string | null): RepairAction | null {
  if (!action) return null
  return action.toLowerCase().startsWith("replace") ? "replace" : "repair"
}

// Human label for an area — prefer the silhouette zone label, fall back to a
// title-cased section name for unmapped areas.
function areaLabel(
  zone: DamageZone | null,
  section: VehicleSection,
  component: string | null,
): string {
  if (zone) return DAMAGE_ZONE_COPY[zone]
  const src = component || section
  return src
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

const SEVERITY_RANK: Record<Severity, number> = { minor: 1, moderate: 2, severe: 3 }

export function summarizeDamage(lineItems: ApiEstimateLineItem[]): DamageReadout {
  const active = lineItems.filter((li) => li.isActive !== false)
  const areas: DetectedArea[] = active.map((li) => {
    const zone = mapToZone(li)
    return {
      zone,
      label: areaLabel(zone, li.section, li.component),
      severity: severityFrom(li),
      action: actionFrom(li.action),
      highConfidence: (li.confidence ?? "").toLowerCase() === "high",
    }
  })

  const zones = Array.from(
    new Set(areas.map((a) => a.zone).filter(Boolean) as DamageZone[]),
  )

  const worst = areas.reduce<Severity>((acc, a) => {
    return SEVERITY_RANK[a.severity] > SEVERITY_RANK[acc] ? a.severity : acc
  }, "minor")

  return { areas, zones, headline: buildHeadline(zones), worst }
}

// Plain-language read of WHERE the damage is — never how much it costs.
function buildHeadline(zones: DamageZone[]): string {
  if (zones.length === 0) return "We’ve flagged the damage from your photos"
  const front = zones.some((z) =>
    ["front-bumper", "hood", "front-fender-left", "front-fender-right"].includes(z),
  )
  const rear = zones.some((z) =>
    ["rear-bumper", "trunk", "quarter-panel-left", "quarter-panel-right"].includes(z),
  )
  const leftSide = zones.some((z) => z.endsWith("-left"))
  const rightSide = zones.some((z) => z.endsWith("-right"))

  if (front && rear) return "Damage front and rear"
  if (front) return "Front-end damage"
  if (rear) return "Rear-end damage"
  if (leftSide && !rightSide) return "Driver-side damage"
  if (rightSide && !leftSide) return "Passenger-side damage"
  return "We spotted the damage"
}

export const SEVERITY_LABEL: Record<Severity, string> = {
  minor: "Minor",
  moderate: "Moderate",
  severe: "Severe",
}
